import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./UtentiTable.module.css";
import {
  FiPlus,
  FiSearch,
  FiCheckCircle,
  FiSlash,
  FiArchive,
  FiRotateCcw,
  FiToggleLeft,
  FiToggleRight,
} from "react-icons/fi";
import { API_BASE } from "../api";

function UtentiTable({
  fetchUrl,
  archived = false,
  activeTab = "utenti",
  onAddUser,
  onRowClick,
}) {
  const navigate = useNavigate();

  const effectiveFetchUrl =
    fetchUrl || `${API_BASE}/utenti${archived ? "/archiviati" : ""}`;

  const storageKey = archived
    ? "utentiTableState:archiviati"
    : "utentiTableState:attivi";

  const listStorageKey = archived
    ? "utentiTableList:archiviati"
    : "utentiTableList:attivi";

  // dati
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const CONTRACT_OPTIONS = [
    { value: "full_time", label: "Full time 6,40" },
    { value: "part_time_2", label: "Part time 2" },
    { value: "part_time_3", label: "Part time 3" },
    { value: "part_time_4", label: "Part time 4" },
    { value: "part_time_6", label: "Part time 6" },
    { value: "part_time_8", label: "Part time 8" },
    { value: "chiamata_6", label: "Chiamata 6" },
  ];

  const [bulkContract, setBulkContract] = useState("");

  // UI state
  const readSavedState = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const [q, setQ] = useState(() => readSavedState()?.q ?? "");
  const [filterSede, setFilterSede] = useState(() => readSavedState()?.filterSede ?? "tutte");
  const [filterSocieta, setFilterSocieta] = useState(() => readSavedState()?.filterSocieta ?? "tutte");
  const [filterStato, setFilterStato] = useState(() => readSavedState()?.filterStato ?? "tutti");
  const [sortBy, setSortBy] = useState(() => readSavedState()?.sortBy ?? "nome");
  const [sortDir, setSortDir] = useState(() => readSavedState()?.sortDir ?? "asc");

  const [pageSize, setPageSize] = useState(() => readSavedState()?.pageSize ?? 100);
  const [page, setPage] = useState(() => readSavedState()?.page ?? 1);

  // selezione multipla
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // fetch
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setSelectedIds([]);

        const res = await fetch(effectiveFetchUrl, {
          headers: { Accept: "application/json, text/plain, */*" },
        });

        const ctype = res.headers.get("content-type") || "";
        const isJson = ctype.includes("application/json");

        if (!res.ok) {
          const msg = isJson
            ? (await res.json().catch(() => null))?.message || `HTTP ${res.status}`
            : `HTTP ${res.status} — non JSON`;
          throw new Error(msg);
        }

        const data = isJson ? await res.json() : [];
        if (alive) setUtenti(Array.isArray(data) ? data : data?.items || []);
      } catch (e) {
        if (alive) setError(e.message || "Errore di caricamento");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [effectiveFetchUrl]);

  // reset pagina quando cambia vista
  useEffect(() => {
    setSelectedIds([]);
  }, [archived]);

  // "Acquapark Egnazia, Ingresso" -> "Acquapark Egnazia"
  const baseSede = (s) => String(s || "").split(",")[0].trim();

  const sedi = useMemo(() => {
    const m = new Map();
    utenti.forEach((u) => {
      const b = baseSede(u?.sede);
      if (b && !m.has(b.toLowerCase())) m.set(b.toLowerCase(), b);
    });
    return [
      "tutte",
      ...Array.from(m.values()).sort((a, b) =>
        a.localeCompare(b, "it", { sensitivity: "base" })
      ),
    ];
  }, [utenti]);

  const societa = useMemo(() => {
    const s = new Set();
    utenti.forEach((u) => u?.societa_nome && s.add(u.societa_nome));
    return ["tutte", ...Array.from(s).sort()];
  }, [utenti]);

  const filtered = useMemo(() => {
    const QQ = q.trim().toLowerCase();

    let out = utenti.filter((u) => {
      const nome = `${u?.nome || ""} ${u?.cognome || ""}`.trim();
      const hay = [
        nome.toLowerCase(),
        (u?.email || "").toLowerCase(),
        (u?.ruolo || "").toLowerCase(),
        (u?.sede || "").toLowerCase(),
        (u?.societa_nome || "").toLowerCase(),
      ].join(" ");

      if (QQ && !hay.includes(QQ)) return false;
      if (filterSede !== "tutte") {
        const sel = filterSede.trim().toLowerCase();
        const full = String(u?.sede || "").trim().toLowerCase();
        // match sulla sede base (include tutte le sotto-sedi, es. "…, Ingresso")
        // oppure sul valore completo (retro-compatibilità con filtri salvati)
        if (baseSede(u?.sede).toLowerCase() !== sel && full !== sel) return false;
      }
      if (filterSocieta !== "tutte" && u?.societa_nome !== filterSocieta) return false;
      if (filterStato === "attivi" && !u?.stato_attivo) return false;
      if (filterStato === "disattivi" && !!u?.stato_attivo) return false;
      return true;
    });

    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => {
      const m = (v) => (v ?? "").toString().toLowerCase();
      switch (sortBy) {
        case "email":
          return m(a.email).localeCompare(m(b.email)) * dir;
        case "sede":
          return m(a.sede).localeCompare(m(b.sede)) * dir;
        case "stato":
          return ((a.stato_attivo ? 1 : 0) - (b.stato_attivo ? 1 : 0)) * dir;
        case "nome":
        default: {
          const an = `${m(a.cognome)} ${m(a.nome)}`.trim();
          const bn = `${m(b.cognome)} ${m(b.nome)}`.trim();
          return an.localeCompare(bn) * dir;
        }
      }
    });

    return out;
  }, [utenti, q, filterSede, filterSocieta, filterStato, sortBy, sortDir]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          q,
          filterSede,
          filterSocieta,
          filterStato,
          sortBy,
          sortDir,
          pageSize,
          page,
        })
      );
    } catch {
      // ignore storage errors
    }
  }, [storageKey, q, filterSede, filterSocieta, filterStato, sortBy, sortDir, pageSize, page]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        listStorageKey,
        JSON.stringify(filtered.map((u) => u.id).filter(Boolean))
      );
    } catch {
      // ignore storage errors
    }
  }, [listStorageKey, filtered]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const pageItems = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = totalItems === 0 ? 0 : Math.min(totalItems, page * pageSize);

  const pageItemIds = useMemo(
    () => pageItems.map((u) => u.id).filter(Boolean),
    [pageItems]
  );
  const selectedUsers = useMemo(
    () => utenti.filter((u) => selectedIds.includes(u.id)),
    [utenti, selectedIds]
  );

  const selectedStates = useMemo(
    () => Array.from(new Set(selectedUsers.map((u) => !!u.stato_attivo))),
    [selectedUsers]
  );

  const selectedHaveUniformState = selectedStates.length <= 1;
  const selectedStateValue =
    selectedStates.length === 1 ? selectedStates[0] : null;

  const allPageSelected =
    pageItemIds.length > 0 && pageItemIds.every((id) => selectedIds.includes(id));

  const somePageSelected =
    pageItemIds.some((id) => selectedIds.includes(id)) && !allPageSelected;

  const toggleSort = (key) => {
    if (key === sortBy) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const goAdd = () => {
    if (typeof onAddUser === "function") onAddUser();
    else navigate("/utenti/nuovo");
  };

  const handleRowClick = useCallback((u) => {
    if (typeof onRowClick === "function") {
      onRowClick(u);
      return;
    }

    navigate(`/utenti/${u?.id || ""}`, {
      state: {
        listStorageKey,
        tableStateKey: storageKey,
        activeTab,
        backTo: "/utenti",
      },
    });
  }, [navigate, onRowClick, listStorageKey, storageKey, activeTab]);

  const toggleUserSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllPage = () => {
    setSelectedIds((prev) => {
      if (allPageSelected) {
        return prev.filter((id) => !pageItemIds.includes(id));
      }
      return Array.from(new Set([...prev, ...pageItemIds]));
    });
  };

  const reloadData = async () => {
    const res = await fetch(effectiveFetchUrl, {
      headers: { Accept: "application/json, text/plain, */*" },
    });
    const data = await res.json();
    setUtenti(Array.isArray(data) ? data : data?.items || []);
  };
  const handleBulkToggleStatus = async () => {
    if (!selectedIds.length || bulkLoading) return;

    if (!selectedHaveUniformState) {
      alert("Gli utenti selezionati non hanno tutti lo stesso stato.");
      return;
    }

    const nextState = !selectedStateValue;
    const label = nextState ? "attivi" : "non attivi";

    const ok = window.confirm(
      `Vuoi impostare ${selectedIds.length} dipendenti come ${label}?`
    );
    if (!ok) return;

    try {
      setBulkLoading(true);

      const res = await fetch(`${API_BASE}/utenti/bulk/stato-attivo`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain, */*",
        },
        body: JSON.stringify({
          ids: selectedIds,
          stato_attivo: nextState,
        }),
      });

      const ctype = res.headers.get("content-type") || "";
      const isJson = ctype.includes("application/json");
      const payload = isJson ? await res.json().catch(() => null) : null;

      if (!res.ok) {
        throw new Error(payload?.error || payload?.message || `HTTP ${res.status}`);
      }

      await reloadData();
      setSelectedIds([]);
    } catch (e) {
      alert(e.message || "Errore durante cambio stato");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkContractChange = async () => {
    if (!selectedIds.length || !bulkContract || bulkLoading) return;

    const label =
      CONTRACT_OPTIONS.find((c) => c.value === bulkContract)?.label || bulkContract;

    const ok = window.confirm(
      `Vuoi impostare il contratto "${label}" a ${selectedIds.length} dipendenti?`
    );
    if (!ok) return;

    try {
      setBulkLoading(true);

      const res = await fetch(`${API_BASE}/utenti/bulk/tipo-contratto`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain, */*",
        },
        body: JSON.stringify({
          ids: selectedIds,
          tipo_contratto: bulkContract,
        }),
      });

      const ctype = res.headers.get("content-type") || "";
      const isJson = ctype.includes("application/json");
      const payload = isJson ? await res.json().catch(() => null) : null;

      if (!res.ok) {
        throw new Error(payload?.error || payload?.message || `HTTP ${res.status}`);
      }

      await reloadData();
      setSelectedIds([]);
      setBulkContract("");
    } catch (e) {
      alert(e.message || "Errore durante cambio contratto");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSingleArchive = async (id) => {
    const ok = window.confirm("Vuoi archiviare questo dipendente?");
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE}/utenti/${id}/archivia`, {
        method: "PATCH",
        headers: {
          Accept: "application/json, text/plain, */*",
        },
      });

      const ctype = res.headers.get("content-type") || "";
      const isJson = ctype.includes("application/json");
      const payload = isJson ? await res.json().catch(() => null) : null;

      if (!res.ok) {
        throw new Error(payload?.error || payload?.message || `HTTP ${res.status}`);
      }

      await reloadData();
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (e) {
      alert(e.message || "Errore durante archiviazione");
    }
  };

  const handleBulkAction = async () => {
    if (!selectedIds.length || bulkLoading) return;

    const actionLabel = archived ? "ripristinare" : "archiviare";
    const ok = window.confirm(
      `Vuoi ${actionLabel} ${selectedIds.length} dipendenti?`
    );
    if (!ok) return;

    try {
      setBulkLoading(true);

      const endpoint = archived
        ? `${API_BASE}/utenti/bulk/ripristina`
        : `${API_BASE}/utenti/bulk/archivia`;

      const res = await fetch(endpoint, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain, */*",
        },
        body: JSON.stringify({ ids: selectedIds }),
      });

      const ctype = res.headers.get("content-type") || "";
      const isJson = ctype.includes("application/json");
      const payload = isJson ? await res.json().catch(() => null) : null;

      if (!res.ok) {
        throw new Error(payload?.error || payload?.message || `HTTP ${res.status}`);
      }

      await reloadData();
      setSelectedIds([]);
    } catch (e) {
      alert(e.message || "Errore durante operazione multipla");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSingleRestore = async (id) => {
    const ok = window.confirm("Vuoi ripristinare questo dipendente?");
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE}/utenti/${id}/ripristina`, {
        method: "PATCH",
        headers: {
          Accept: "application/json, text/plain, */*",
        },
      });

      const ctype = res.headers.get("content-type") || "";
      const isJson = ctype.includes("application/json");
      const payload = isJson ? await res.json().catch(() => null) : null;

      if (!res.ok) {
        throw new Error(payload?.error || payload?.message || `HTTP ${res.status}`);
      }

      await reloadData();
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (e) {
      alert(e.message || "Errore durante ripristino");
    }
  };

  const PaginationBar = () => (
    <div className={styles.paginationBar}>
      <div className={styles.paginationLeft}>
        <label className={styles.pageSizeLabel}>Mostra</label>
        <select
          className={styles.pageSizeSelect}
          value={pageSize}
          onChange={(e) => {
            const newSize = Number(e.target.value);
            setPage(1);
            setPageSize(newSize);
          }}
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      <div className={styles.paginationCenter}>
        <span>Totale: {totalItems}</span>
        <span className={styles.dot}>•</span>
        <span>Mostrati {from}–{to}</span>
        <span className={styles.dot}>•</span>
        <span>
          Pagina {page} di {totalPages}
        </span>
      </div>

      <div className={styles.paginationControls}>
        <button
          className={styles.pageBtn}
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
        >
          ←
        </button>

        <button
          className={styles.pageBtn}
          disabled={page === totalPages || totalItems === 0}
          onClick={() => setPage((p) => p + 1)}
        >
          →
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {archived ? "Dipendenti archiviati" : "Utenti"}
        </h1>

        <div className={styles.headerCta}>
          {!!selectedIds.length && !archived && (
            <>
              <button
                className={`btn btn-primary ${styles.addBtn}`}
                onClick={handleBulkAction}
                disabled={bulkLoading}
              >
                <FiArchive />
                {bulkLoading
                  ? "Operazione in corso..."
                  : ` Archivia selezionati (${selectedIds.length})`}
              </button>

              <button
                className={`btn btn-primary ${styles.addBtn}`}
                onClick={handleBulkToggleStatus}
                disabled={bulkLoading || !selectedHaveUniformState}
                title={
                  selectedHaveUniformState
                    ? ""
                    : "Gli utenti selezionati non hanno tutti lo stesso stato"
                }
              >
                {selectedStateValue ? <FiToggleLeft /> : <FiToggleRight />}
                {selectedHaveUniformState
                  ? selectedStateValue
                    ? ` Imposta non attivi (${selectedIds.length})`
                    : ` Imposta attivi (${selectedIds.length})`
                  : " Stato misto"}
              </button>

              <select
                className="select"
                value={bulkContract}
                onChange={(e) => setBulkContract(e.target.value)}
                disabled={bulkLoading}
              >
                <option value="">Imposta contratto…</option>
                {CONTRACT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <button
                className={`btn btn-primary ${styles.addBtn}`}
                onClick={handleBulkContractChange}
                disabled={bulkLoading || !bulkContract}
              >
                Applica contratto ({selectedIds.length})
              </button>
            </>
          )}

          {!!selectedIds.length && archived && (
            <button
              className={`btn btn-secondary ${styles.addBtn}`}
              onClick={handleBulkAction}
              disabled={bulkLoading}
            >
              <FiRotateCcw />
              {bulkLoading
                ? "Operazione in corso..."
                : ` Ripristina selezionati (${selectedIds.length})`}
            </button>
          )}

          {!archived && (
            <button className={`btn btn-primary ${styles.addBtn}`} onClick={goAdd}>
              <FiPlus /> Nuovo utente
            </button>
          )}
        </div>
      </div>

      <div className={styles.underline} />

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          {q === "" ? <FiSearch className={styles.searchIcon} /> : null}
          <input
            className={`input ${styles.search}`}
            placeholder="     Cerca nome, cognome, email…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className={styles.filters}>
          <select
            className="select"
            value={filterSede}
            onChange={(e) => {
              setFilterSede(e.target.value);
              setPage(1);
            }}
          >
            {sedi.map((s) => (
              <option key={s} value={s}>
                {s === "tutte" ? "Tutte le sedi" : s}
              </option>
            ))}
          </select>

          <select
            className="select"
            value={filterSocieta}
            onChange={(e) => {
              setFilterSocieta(e.target.value);
              setPage(1);
            }}
          >
            {societa.map((s) => (
              <option key={s} value={s}>
                {s === "tutte" ? "Tutte le società" : s}
              </option>
            ))}
          </select>

          <select
            className="select"
            value={filterStato}
            onChange={(e) => {
              setFilterStato(e.target.value);
              setPage(1);
            }}
          >
            <option value="tutti">Tutti gli stati</option>
            <option value="attivi">Solo attivi</option>
            <option value="disattivi">Solo disattivi</option>
          </select>
        </div>
      </div>

      {!!selectedIds.length && !archived && !selectedHaveUniformState && (
        <div className={styles.empty} style={{ marginBottom: 12 }}>
          Gli utenti selezionati non hanno tutti lo stesso stato. Per cambiare
          stato in blocco, seleziona solo utenti tutti attivi oppure tutti non attivi.
        </div>
      )}

      <div className={`card ${styles.tableCard}`}>
        {loading && <div className={styles.empty}>Caricamento…</div>}
        {error && !loading && <div className={styles.empty}>Errore: {error}</div>}

        {!loading && !error && (
          <>
            <PaginationBar />

            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  <th className={styles.th} style={{ width: 44 }}>
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = somePageSelected;
                      }}
                      onChange={toggleSelectAllPage}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>

                  <th
                    className={styles.th}
                    onClick={() => toggleSort("nome")}
                    style={{ cursor: "pointer" }}
                  >
                    Cognome / Nome{" "}
                    {sortBy === "nome" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>

                  <th
                    className={styles.th}
                    onClick={() => toggleSort("email")}
                    style={{ cursor: "pointer" }}
                  >
                    Email {sortBy === "email" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>

                  <th
                    className={styles.th}
                    onClick={() => toggleSort("sede")}
                    style={{ cursor: "pointer" }}
                  >
                    Sede {sortBy === "sede" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>

                  <th
                    className={styles.th}
                    onClick={() => toggleSort("stato")}
                    style={{ cursor: "pointer", width: 120 }}
                  >
                    Stato {sortBy === "stato" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>

                  <th className={styles.th} style={{ width: archived ? 130 : 50 }} />
                </tr>
              </thead>

              <tbody>
                {pageItems.map((u) => {
                  const nomeCompleto = `${u?.cognome || ""} ${u?.nome || ""}`.trim();
                  const isSelected = selectedIds.includes(u.id);

                  return (
                    <tr
                      key={u?.id || nomeCompleto + u?.email}
                      className={styles.row}
                      onClick={() => handleRowClick(u)}
                    >
                      <td
                        className={styles.td}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleUserSelection(u.id)}
                        />
                      </td>

                      <td className={styles.td}>{nomeCompleto || "—"}</td>
                      <td className={styles.td}>{u?.email || "—"}</td>
                      <td className={styles.td}>{u?.sede || "—"}</td>

                      <td className={styles.td}>
                        {u?.stato_attivo ? (
                          <span className={styles.badgeOk}>
                            <FiCheckCircle /> Attivo
                          </span>
                        ) : (
                          <span className={styles.badgeNo}>
                            <FiSlash /> Non attivo
                          </span>
                        )}
                      </td>

                      <td
                        className={styles.td}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {archived ? (
                          <button
                            type="button"
                            className="btn btn-secondary arch"
                            onClick={() => handleSingleRestore(u.id)}
                          >
                            <FiRotateCcw />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-secondary arch"
                            onClick={() => handleSingleArchive(u.id)}
                            title="Archivia dipendente"
                          >
                            <FiArchive />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!pageItems.length && (
                  <tr>
                    <td className={styles.td} colSpan={6}>
                      <div className={styles.empty}>
                        Nessun risultato con i filtri correnti.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <PaginationBar />
          </>
        )}
      </div>
    </div>
  );
}

export default UtentiTable;